import { useState } from 'react';
import { useNavigate, useRouter, getRouteApi } from '@tanstack/react-router';
import { Button } from '@workpay/ds-shared/components/ui/Button';
import { useTranslation } from '@hooks/useTranslation';
import { useBfcacheRestore } from '@hooks/useBfcacheRestore';
import { EorAuthLayout } from './EorAuthLayout';
import { StepTitle, StepSub } from './components/StepBits';
import { redirectToUrl } from '@lib/loginRedirect';
import type { WorkspaceTile } from '@app-types/workspaceDiscovery';
import form from './EorForm.module.scss';
import styles from './WorkspacePicker.module.scss';

const routeApi = getRouteApi('/eor/select-workspace');

/** Shown while the route loader runs verify-challenge. */
export function PickerPending() {
  const { t } = useTranslation();
  return (
    <EorAuthLayout>
      <p className={styles.loading}>{t('K_DS_EXT_EOR_LOADING', 'Loading…')}</p>
    </EorAuthLayout>
  );
}

/** Shown when the loader throws (network / 5xx — token NOT consumed). */
export function PickerError() {
  const { t } = useTranslation();
  const router = useRouter();
  return (
    <EorAuthLayout>
      <StepTitle>{t('K_DS_EXT_EOR_LINK_PROBLEM_TITLE', 'Something went wrong')}</StepTitle>
      <StepSub>{t('K_DS_EXT_EOR_GENERIC_RETRY', 'This is usually temporary. Please try again.')}</StepSub>
      <Button variant="primary" size="lg" className={form.cta} onClick={() => router.invalidate()}>
        {t('K_DS_EXT_ACTION_TRY_AGAIN', 'Try again')}
      </Button>
    </EorAuthLayout>
  );
}

export function WorkspacePicker() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const data = routeApi.useLoaderData();
  const [selected, setSelected] = useState<string | null>(null);
  // Hold busy across the full-page redirect (same as WorkspaceNameEntry).
  const [redirecting, setRedirecting] = useState(false);

  // Back after the redirect restores this page from the bfcache with
  // `redirecting` still true — reset so the tiles/CTA are interactive again.
  useBfcacheRestore(() => setRedirecting(false));

  // Verify failed (expired / invalid / consumed / attempt-cap) — workspaces null,
  // messageCode carries the reason. (Empty list is redirected in the loader.)
  if (data.workspaces === null) {
    const base = data.messageCode
      ? t(data.messageCode, 'This link is no longer valid. Request a new one.')
      : t('K_DS_EXT_EOR_LINK_INVALID', 'This link is no longer valid. Request a new one.');
    const msg =
      typeof data.attemptsRemaining === 'number'
        ? `${base} ${t('K_DS_EXT_EOR_ATTEMPTS_LEFT', '{n} attempts remaining').replace('{n}', String(data.attemptsRemaining))}`
        : base;
    return (
      <EorAuthLayout>
        <StepTitle>{t('K_DS_EXT_EOR_LINK_PROBLEM_TITLE', 'Link problem')}</StepTitle>
        <StepSub>{msg}</StepSub>
        <Button variant="primary" size="lg" className={form.cta} onClick={() => navigate({ to: '/eor/find' })}>
          {t('K_DS_EXT_EOR_TRY_DIFF_EMAIL', 'Try a different email')}
        </Button>
      </EorAuthLayout>
    );
  }

  const workspaces = data.workspaces;
  const chosen = workspaces.find((w) => w.subdomain === selected);

  function go() {
    if (chosen?.loginUrl) {
      setRedirecting(true);
      redirectToUrl(chosen.loginUrl);
    }
  }

  return (
    <EorAuthLayout>
      <StepTitle>{t('K_DS_EXT_EOR_PICK_TITLE', 'Select your workspace')}</StepTitle>
      <StepSub>
        {t(
          'K_DS_EXT_EOR_PICK_SUB',
          'Your email is linked to multiple workspaces. Choose the one you want to sign into.',
        )}
      </StepSub>

      {/* Plain labelled button list — each button is a native tab stop and
          Enter/Space-operable; aria-pressed conveys the current selection. */}
      <div className={styles.list} role="group" aria-label={t('K_DS_EXT_EOR_PICK_TITLE', 'Select your workspace')}>
        {workspaces.map((ws: WorkspaceTile) => {
          const inactive = !ws.active;
          const isSel = ws.subdomain === selected;
          return (
            <button
              key={ws.subdomain}
              type="button"
              aria-pressed={isSel}
              disabled={inactive || redirecting}
              className={`${styles.card} ${isSel ? styles.cardSelected : ''} ${inactive ? styles.cardInactive : ''}`}
              onClick={() => setSelected(ws.subdomain)}
            >
              <span className={styles.avatar}>{ws.orgName.charAt(0).toUpperCase()}</span>
              <span className={styles.cardText}>
                <span className={styles.cardName}>{ws.orgName}</span>
                <span className={styles.cardHost}>{ws.domain}</span>
              </span>
              {inactive ? (
                <span className={styles.badge}>{t('K_DS_EXT_EOR_INACTIVE', 'Inactive')}</span>
              ) : (
                <span className={`${styles.radio} ${isSel ? styles.radioOn : ''}`} aria-hidden="true" />
              )}
            </button>
          );
        })}
      </div>

      <Button
        variant="primary"
        size="lg"
        loading={redirecting}
        disabled={!chosen || redirecting}
        className={form.cta}
        onClick={go}
      >
        {t('K_DS_EXT_EOR_GO_TO_WORKSPACE', 'Go to workspace')}
      </Button>

      <p className={form.footerText}>
        {t('K_DS_EXT_EOR_NOT_SEEING', 'Not seeing your workspace?')}{' '}
        <button type="button" className={form.link} onClick={() => navigate({ to: '/eor/find' })}>
          {t('K_DS_EXT_EOR_TRY_DIFF_EMAIL', 'Try a different email')}
        </button>
      </p>
    </EorAuthLayout>
  );
}
